import key from 'weak-key';
import { Col, Container, Row, Tabs, Tab, Dropdown } from '@geberit/gdds';

// components
import { Headline, FontWeights, Formats } from '../../ContentElements/Headline';
import Content from './content';
import { InnerHtml } from '../../InnerHtml/inner-html';

// styles
import styles from './tabs.module.scss';

// utils
import { useSectionId } from 'utils/hooks/use-section-id';
import { decodingContent } from 'utils/decodingContent';
import { buildSize, gridSizes } from 'utils/gridSize';
import { useKolo } from 'utils/hooks/use-kolo';

interface TabViewProps {
  isMobile: boolean;
  tabsContent: any[];
  catalogName: string;
  title: string;
  subtitle?: string;
  anchor?: string;
  titleInAnchor?: boolean;
  contentIndex?: number;
  dropdownTitles?: any[];
  tabTitles?: any[];
  onChange: (e: any) => void;
  currentIndex: number;
  description?: string;
  position?: string;
}

function TabView({
  isMobile,
  tabsContent,
  catalogName,
  title,
  subtitle,
  anchor = '',
  titleInAnchor = false,
  contentIndex,
  dropdownTitles,
  tabTitles,
  onChange,
  currentIndex,
  description,
  position,
}: Readonly<TabViewProps>) {
  const isKolo = useKolo();

  const size = position === 'full' ? 'twoThird' : 'full';

  return (
    <div
      id={useSectionId(title ?? '', anchor ?? '', titleInAnchor || false, contentIndex || 0)}
      data-preview-id={catalogName ? `#${catalogName}` : undefined}
    >
      <div className={styles.tabHeader}>
        <Container maxContentWidth={buildSize(gridSizes[size])}>
          {(title || subtitle) && (
            <Row>
              <Col>
                <div className={styles.headline}>
                  <Headline
                    format={Formats.h1}
                    tag={Formats.h2}
                    title={title}
                    subtitle={subtitle}
                    titleFontWeight={FontWeights.bold}
                    subtitleFontWeight={isKolo ? FontWeights.bold : FontWeights.light}
                  />
                </div>
                <InnerHtml as="span" content={description} className={styles.description} isGdds />
              </Col>
            </Row>
          )}
          <Row>
            <Col className={styles.tabTitles}>
              {!isMobile && (
                <Tabs
                  tabPanelPosition="bottom"
                  initialSelected={currentIndex}
                  alignment="horizontal"
                  onTabChange={onChange}
                >
                  {tabTitles.map((tabTitle) => (
                    <Tab key={key({ tabTitle })} title={decodingContent(tabTitle)} />
                  ))}
                </Tabs>
              )}
              {isMobile && (
                <div className={styles.tabDropdownWrapper}>
                  <Dropdown
                    name={`dropdown-${contentIndex}`}
                    onChange={onChange}
                    items={dropdownTitles}
                    outlined
                    values={{ [`dropdown-${contentIndex}`]: dropdownTitles[currentIndex]?.value }}
                  />
                </div>
              )}
            </Col>
          </Row>
        </Container>
      </div>
      <div className={styles.tabContent}>
        {tabsContent.map((item, i) => (
          <div key={key(item)} className={currentIndex === i ? styles.visible : styles.invisible}>
            <Content content={item} currentIndex={currentIndex} childOf="tab" isChild />
          </div>
        ))}
      </div>
    </div>
  );
}

export default TabView;
